import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchServiceBanner, updateServiceBanner, type AdminServiceBanner } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

const SEVERITIES = ['info', 'warning', 'critical'] as const

const PREVIEW_CLS: Record<string, string> = {
  info: 'border-accent bg-accent-subtle text-accent',
  warning: 'border-warning bg-warning-subtle text-warning',
  critical: 'border-danger bg-danger-subtle text-danger',
}

/** A15.20 — xizmat/maintenance banneri. Admin qo'yadi, app public o'qiydi. */
export function ServiceBanner() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [b, setB] = useState<AdminServiceBanner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchServiceBanner(ac.signal)
      .then((r) => {
        if (alive) setB(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const save = async () => {
    if (!b || busy) return
    setBusy(true)
    try {
      await updateServiceBanner(b)
      toast(t.broadcast.settingsSaved, { tone: 'success' })
    } catch (e) {
      toast(t.broadcast.createFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={t.broadcast.banner}
        screenId="A15.20"
        description={t.broadcast.bannerNote}
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.broadcast.title}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-md space-y-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} />
        ) : !b ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-md space-y-4">
            {b.text && (
              <div className={`rounded-md border px-3 py-2 text-sm ${PREVIEW_CLS[b.severity] ?? PREVIEW_CLS.info}`}>
                {b.text}
              </div>
            )}
            <div className="space-y-4 rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-secondary">{t.broadcast.bannerText}</label>
                <textarea
                  value={b.text}
                  onChange={(e) => setB({ ...b, text: e.target.value })}
                  rows={2}
                  className="rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-secondary">{t.broadcast.severity}</label>
                <select
                  value={b.severity}
                  onChange={(e) => setB({ ...b, severity: e.target.value })}
                  className="w-40 rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {t.broadcast[s]}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-primary">
                <input type="checkbox" checked={b.enabled} onChange={(e) => setB({ ...b, enabled: e.target.checked })} />
                {t.broadcast.bannerEnabled}
              </label>
              <button
                disabled={busy}
                onClick={save}
                className="rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
              >
                {t.common.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
